import { SEO_DEFAULTS } from "@/constants/config";
import { useEffect } from "react";

export interface SeoProps {
  title: string;
  description: string;
  /** Canonical path, e.g. `/en/` — the origin is filled in automatically. */
  path: string;
  keywords?: string;
  locale?: string;
  imagePath?: string;
  /** Locale alternates for `hreflang`. */
  alternates?: readonly { hreflang: string; path: string }[];
  noindex?: boolean;
}

const upsertMeta = (key: "name" | "property", value: string, content: string): void => {
  const selector = `meta[${key}="${value}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(key, value);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string, hreflang?: string): void => {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    if (hreflang) element.setAttribute("hreflang", hreflang);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const absolute = (path: string, origin: string): string =>
  path.startsWith("http") ? path : `${origin}${path.startsWith("/") ? path : `/${path}`}`;

/**
 * Per-route document metadata (agents.md §4): Open Graph, Twitter card, canonical
 * and hreflang alternates. Rendered as a component so a route declares its own
 * metadata declaratively, without pulling in a Helmet dependency.
 */
export const Seo = ({
  title,
  description,
  path,
  keywords,
  locale = SEO_DEFAULTS.ogType === "website" ? "en" : "en",
  imagePath = SEO_DEFAULTS.ogImagePath,
  alternates = [],
  noindex = false,
}: SeoProps): null => {
  useEffect(() => {
    const origin = window.location.origin;
    const url = absolute(path, origin);
    const image = absolute(imagePath, origin);

    document.title = title;
    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : SEO_DEFAULTS.robots);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", SEO_DEFAULTS.ogType);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:locale", locale);

    upsertMeta("name", "twitter:card", SEO_DEFAULTS.twitterCard);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    upsertLink("canonical", url);
    for (const alternate of alternates) {
      upsertLink("alternate", absolute(alternate.path, origin), alternate.hreflang);
    }
  }, [title, description, path, keywords, locale, imagePath, alternates, noindex]);

  return null;
};
